import { useMemo } from "react";

import type { AiTrackerReportsBrandOverviewPeriodChangesLlmBreakdown } from "@similarweb/pro-api-client";
import { colorsPalettes } from "@similarweb/styles";
import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { ExplicitLLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";

import type { PeriodChangesCountKey } from "./types";

const getLlmDisplayName = (llm: string): string => {
    const modelConfig = AI_MODELS_CONFIG[llm as ExplicitLLModel];
    return modelConfig ? translate(modelConfig.displayNameKey) : llm;
};

const TOOLTIP_WIDTH = 255;

interface ILlmBreakdownTooltipProps {
    title: string;
    metricKey: PeriodChangesCountKey;
    llmBreakdown: AiTrackerReportsBrandOverviewPeriodChangesLlmBreakdown[];
}

interface ITooltipRow {
    items: { label: string; llm: ExplicitLLModel };
    fieldValue: number;
}

export const LlmBreakdownTooltip = ({ title, metricKey, llmBreakdown }: ILlmBreakdownTooltipProps) => {
    const rows = useMemo<ITooltipRow[]>(
        () =>
            [...llmBreakdown]
                .sort((a, b) => b[metricKey] - a[metricKey])
                .map((row) => ({
                    items: {
                        label: getLlmDisplayName(row.llm),
                        llm: row.llm as ExplicitLLModel,
                    },
                    fieldValue: row[metricKey],
                })),
        [llmBreakdown, metricKey],
    );

    const columnDefinition: ColumnDefinition<ITooltipRow>[] = [
        {
            key: "items",
            title: translate("llm.overview_report.what_changed.tooltip.column.llm"),
            selector: "items" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ items }: ITooltipRow) => {
                const modelConfig = AI_MODELS_CONFIG[items.llm];
                const Icon = modelConfig?.Icon;

                return (
                    <Flex
                        alignItems="center"
                        gap="8px"
                    >
                        {Icon ? <Icon size="xs" /> : null}
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[400]}
                        >
                            {items.label}
                        </Text>
                    </Flex>
                );
            },
        },
        {
            key: "fieldValue",
            title: translate("llm.overview_report.what_changed.tooltip.column.count"),
            selector: "fieldValue" as const,
            align: "right",
            size: ChartTooltip.ValueCellDefaultwidth,
            render: ({ fieldValue }: ITooltipRow) => <ChartTooltip.ValueCell value={fieldValue.toString()} />,
        },
    ];

    return (
        <Box
            width={`${TOOLTIP_WIDTH}px`}
            data-automation="what-changed-llm-breakdown-tooltip"
        >
            <ChartTooltip
                width={TOOLTIP_WIDTH}
                title={title}
                columnDefinition={columnDefinition}
                data={rows}
            />
        </Box>
    );
};
